import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Check, Clock, Play, Pause, Square, Save, Send, ExternalLink } from 'lucide-react';
import { useTimer } from '@/lib/timer-context';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Link } from 'react-router-dom';

// Helper to format seconds to HH:MM:SS
function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

interface TimesheetEntry {
  id: string;
  projectId: string;
  projectName: string;
  taskId: string;
  taskName: string;
  hours: {
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
    sun: string;
  };
}

interface WeekDay {
  label: string;
  date: string;
  key: keyof TimesheetEntry['hours'];
  isWeekend: boolean;
  fullDate: Date;
}

// Sample projects and tasks for demonstration
const sampleProjects = [
  { id: '1', title: 'Website Redesign' },
  { id: '2', title: 'Mobile App Development' },
  { id: '3', title: 'Brand Strategy' },
  { id: '4', title: 'Internal Activities' },
];

const sampleTasks = [
  { id: '1', projectId: '1', title: 'UI Design' },
  { id: '2', projectId: '1', title: 'Frontend Development' },
  { id: '3', projectId: '2', title: 'API Development' },
  { id: '4', projectId: '2', title: 'Testing' },
  { id: '5', projectId: '3', title: 'Research' },
  { id: '6', projectId: '3', title: 'Strategy Workshop' },
  { id: '7', projectId: '4', title: '1-1 Meeting' },
  { id: '8', projectId: '4', title: 'Admin Work' },
];

// Generate week dates for a given offset
function generateWeekDates(offset: number = 0): { days: WeekDay[]; weekRange: string } {
  const today = new Date();
  const currentDay = today.getDay();
  const diff = currentDay === 0 ? -6 : 1 - currentDay;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff + (offset * 7));

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayKeys: (keyof TimesheetEntry['hours'])[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const days: WeekDay[] = dayNames.map((label, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return {
      label,
      date: `${date.getDate()} ${monthNames[date.getMonth()]}`,
      key: dayKeys[i],
      isWeekend: i >= 5,
      fullDate: date,
    };
  });

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const weekRange = `${monday.getDate()} ${monthNames[monday.getMonth()]} – ${sunday.getDate()} ${monthNames[sunday.getMonth()]} ${sunday.getFullYear()}`;

  return { days, weekRange };
}

export function FigmakeTimesheet() {
  const { isRunning, elapsedSeconds, selectedTask, pauseTimer, resumeTimer, stopTimer } = useTimer();
  
  // Track pause state locally
  const [isPaused, setIsPaused] = useState(false);

  // State
  const [activeTab, setActiveTab] = useState<'timesheet' | 'pending' | 'approved'>('timesheet');
  const [weekOffset, setWeekOffset] = useState(0);
  const [isSaved, setIsSaved] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [entries, setEntries] = useState<TimesheetEntry[]>([createEmptyEntry()]);

  const { days, weekRange } = useMemo(() => generateWeekDates(weekOffset), [weekOffset]);

  // Create empty entry
  function createEmptyEntry(): TimesheetEntry {
    return {
      id: Date.now().toString(),
      projectId: '',
      projectName: '',
      taskId: '',
      taskName: '',
      hours: { mon: '', tue: '', wed: '', thu: '', fri: '', sat: '', sun: '' },
    };
  }

  // Add new row
  const addRow = () => {
    setEntries([...entries, createEmptyEntry()]);
    setIsSaved(false);
  };

  // Delete row
  const deleteRow = (id: string) => {
    if (entries.length > 1) {
      setEntries(entries.filter(e => e.id !== id));
      setIsSaved(false);
    }
  };

  // Update hours
  const updateHours = (id: string, day: keyof TimesheetEntry['hours'], value: string) => {
    // Allow empty, or validate as number
    const cleanValue = value === '' ? '' : parseFloat(value) >= 0 ? value : '';
    setEntries(entries.map(entry =>
      entry.id === id ? { ...entry, hours: { ...entry.hours, [day]: cleanValue } } : entry
    ));
    setIsSaved(false);
  };

  // Update project
  const updateProject = (id: string, projectId: string) => {
    const project = sampleProjects.find(p => p.id === projectId);
    setEntries(entries.map(entry =>
      entry.id === id
        ? { ...entry, projectId, projectName: project?.title || '', taskId: '', taskName: '' }
        : entry
    ));
    setIsSaved(false);
  };

  // Update task
  const updateTask = (id: string, taskId: string) => {
    const task = sampleTasks.find(t => t.id === taskId);
    setEntries(entries.map(entry =>
      entry.id === id
        ? { ...entry, taskId, taskName: task?.title || '' }
        : entry
    ));
    setIsSaved(false);
  };

  // Get tasks for a project
  const getTasksForProject = (projectId: string) => {
    return sampleTasks.filter(t => t.projectId === projectId);
  };

  // Calculate day total
  const calculateDayTotal = (day: keyof TimesheetEntry['hours']) => {
    return entries.reduce((total, entry) => total + (parseFloat(entry.hours[day]) || 0), 0);
  };

  // Calculate row total
  const calculateRowTotal = (entry: TimesheetEntry) => {
    return Object.values(entry.hours).reduce((total, h) => total + (parseFloat(h) || 0), 0);
  };

  // Calculate grand total
  const grandTotal = entries.reduce((total, entry) => total + calculateRowTotal(entry), 0);

  // Handle save
  const handleSave = () => {
    setIsSaved(true);
    console.log('Saving timesheet entries:', entries);
  };

  // Handle submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsSaved(true);
      console.log('Submitted timesheet entries:', entries);
    } catch (error) {
      console.error('Failed to submit timesheet:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Week navigation
  const goToPrevWeek = () => setWeekOffset(weekOffset - 1);
  const goToNextWeek = () => setWeekOffset(weekOffset + 1);
  const goToCurrentWeek = () => setWeekOffset(0);
  
  // Timer controls
  const handlePauseResume = () => {
    if (isPaused) {
      resumeTimer();
      setIsPaused(false);
    } else {
      pauseTimer();
      setIsPaused(true);
    }
  };
  
  const handleStop = () => {
    stopTimer();
    setIsPaused(false);
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-white border-b border-[#e6e6e6] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#4A90E2] flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[18px] font-semibold text-[#262626]">Timesheet</h1>
              <p className="text-[12px] text-[#808080]">Weekly time tracking</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Link to Dashboard */}
            <Link to="/figmake/dashboard">
              <Button variant="outline" size="sm" className="text-[12px]">
                <ExternalLink className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            
            {/* Save Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={isSaved}
              className="text-[12px]"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>

            {/* Submit Button */}
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting || grandTotal === 0}
              className="text-[12px] bg-[#e30840] hover:bg-[#c50735]"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Timer Status Banner */}
        {isRunning && selectedTask && (
          <div className="bg-white rounded-md shadow-sm p-4 mb-6 border-l-4 border-[#e30840]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 relative">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#f5f5f5" strokeWidth="2" />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="#e30840"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="100"
                      strokeDashoffset={isPaused ? 100 : 100 - ((elapsedSeconds % 60) * (100 / 60))}
                      className={isPaused ? '' : 'transition-all duration-1000 ease-linear'}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {isPaused ? (
                      <Play className="w-3 h-3 text-[#e30840]" />
                    ) : (
                      <Pause className="w-3 h-3 text-[#e30840]" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-[#808080] uppercase tracking-wide">
                    {isPaused ? 'Paused' : 'Tracking'}
                  </p>
                  <p className="text-[14px] font-medium text-[#262626]">{selectedTask.title}</p>
                  <p className="text-[12px] text-[#e30840]">{(selectedTask as any).project || (selectedTask as any).project_name || 'No project'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="font-mono text-[20px] font-bold text-[#262626]">
                  {formatTime(elapsedSeconds)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePauseResume}
                  className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white"
                >
                  {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStop}
                  className="border-[#e30840] text-[#e30840] hover:bg-[#e30840] hover:text-white"
                >
                  <Square className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-6 border-b border-[#e7e7e7] mb-6">
          <button
            onClick={() => setActiveTab('timesheet')}
            className={`pb-3 text-[14px] font-medium border-b-2 transition-colors ${
              activeTab === 'timesheet'
                ? 'border-[#e30840] text-[#262626]'
                : 'border-transparent text-[#808080] hover:text-[#262626]'
            }`}
          >
            Timesheet
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 text-[14px] font-medium border-b-2 transition-colors ${
              activeTab === 'pending'
                ? 'border-[#e30840] text-[#262626]'
                : 'border-transparent text-[#808080] hover:text-[#262626]'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`pb-3 text-[14px] font-medium border-b-2 transition-colors ${
              activeTab === 'approved'
                ? 'border-[#e30840] text-[#262626]'
                : 'border-transparent text-[#808080] hover:text-[#262626]'
            }`}
          >
            Approved
          </button>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevWeek}
              className="p-2 hover:bg-[#f6f6f6] rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#262626]" />
            </button>
            <button
              onClick={goToCurrentWeek}
              className="text-[14px] text-[#262626] px-3 py-1 hover:bg-[#f6f6f6] rounded-md transition-colors"
            >
              {weekRange}
            </button>
            <button
              onClick={goToNextWeek}
              className="p-2 hover:bg-[#f6f6f6] rounded-md transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#262626]" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {isSaved && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f0fff4] rounded-md border border-[#27AE60]">
                <Check className="w-4 h-4 text-[#27AE60]" />
                <span className="text-[12px] font-medium text-[#27AE60]">Saved</span>
              </div>
            )}
            <div className="text-[14px] font-medium text-[#262626]">
              Total: <span className="text-[#e30840]">{grandTotal.toFixed(1)}h</span>
            </div>
          </div>
        </div>

        {/* Timesheet Table */}
        <div className="bg-white rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f6f6f6] border-b border-[#e7e7e7]">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] text-[#808080] uppercase tracking-wider font-semibold min-w-[200px]">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] text-[#808080] uppercase tracking-wider font-semibold min-w-[180px]">
                    Task
                  </th>
                  {days.map((day) => (
                    <th
                      key={day.key}
                      className={`px-2 py-3 text-center text-[11px] uppercase tracking-wider font-semibold w-[70px] ${
                        day.isWeekend ? 'text-[#e30840] bg-[#fef5f7]' : 'text-[#808080]'
                      }`}
                    >
                      <div>{day.label}</div>
                      <div className="text-[10px] font-normal mt-0.5">{day.date}</div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center text-[11px] text-[#808080] uppercase tracking-wider font-semibold w-[60px]">
                    Total
                  </th>
                  <th className="px-2 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f5f5f5]">
                {entries.map((entry) => {
                  const projectTasks = entry.projectId ? getTasksForProject(entry.projectId) : [];
                  const rowTotal = calculateRowTotal(entry);

                  return (
                    <tr key={entry.id} className="hover:bg-[#fafafa]">
                      {/* Project */}
                      <td className="px-4 py-3">
                        <Select
                          value={entry.projectId}
                          onValueChange={(value) => updateProject(entry.id, value)}
                        >
                          <SelectTrigger className="w-full text-[13px] border-[#e7e7e7]">
                            <SelectValue placeholder="Select project..." />
                          </SelectTrigger>
                          <SelectContent>
                            {sampleProjects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Task */}
                      <td className="px-4 py-3">
                        <Select
                          value={entry.taskId}
                          onValueChange={(value) => updateTask(entry.id, value)}
                          disabled={!entry.projectId}
                        >
                          <SelectTrigger className="w-full text-[13px] border-[#e7e7e7]">
                            <SelectValue placeholder={entry.projectId ? "Select task..." : "Select project first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {projectTasks.map((task) => (
                              <SelectItem key={task.id} value={task.id}>
                                {task.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Hours for each day */}
                      {days.map((day) => (
                        <td
                          key={day.key}
                          className={`px-1 py-3 ${day.isWeekend ? 'bg-[#fef5f7]' : ''}`}
                        >
                          <input
                            type="text"
                            value={entry.hours[day.key]}
                            onChange={(e) => updateHours(entry.id, day.key, e.target.value)}
                            placeholder="-"
                            className={`w-full px-2 py-1.5 text-center text-[13px] border rounded-md focus:ring-2 focus:ring-[#e30840] focus:border-transparent ${
                              entry.hours[day.key]
                                ? 'border-[#e7e7e7] bg-white'
                                : 'border-transparent bg-[#fafafa]'
                            }`}
                          />
                        </td>
                      ))}

                      {/* Row Total */}
                      <td className="px-3 py-3 text-center">
                        <span className={`text-[13px] font-medium ${rowTotal > 0 ? 'text-[#262626]' : 'text-[#b3b3b3]'}`}>
                          {rowTotal > 0 ? `${rowTotal.toFixed(1)}h` : '-'}
                        </span>
                      </td>

                      {/* Delete Button */}
                      <td className="px-2 py-3">
                        {entries.length > 1 && (
                          <button
                            onClick={() => deleteRow(entry.id)}
                            className="p-1 hover:bg-[#fde8eb] rounded-md transition-colors"
                          >
                            <X className="w-4 h-4 text-[#808080] hover:text-[#e30840]" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Day Totals Row */}
                <tr className="bg-[#f6f6f6] font-medium">
                  <td colSpan={2} className="px-4 py-3 text-[12px] text-[#808080] uppercase">
                    Daily Totals
                  </td>
                  {days.map((day) => {
                    const dayTotal = calculateDayTotal(day.key);
                    return (
                      <td
                        key={day.key}
                        className={`px-1 py-3 text-center text-[12px] ${
                          day.isWeekend ? 'bg-[#fef0f2]' : ''
                        } ${dayTotal > 0 ? 'text-[#262626]' : 'text-[#b3b3b3]'}`}
                      >
                        {dayTotal > 0 ? `${dayTotal.toFixed(1)}h` : '-'}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-center text-[13px] text-[#e30840] font-semibold">
                    {grandTotal.toFixed(1)}h
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Add Row Button */}
          <div className="px-4 py-3 border-t border-[#f5f5f5]">
            <button
              onClick={addRow}
              className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-[#e30840] hover:bg-[#fef5f7] rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
